import React, { useCallback } from 'react';
import { ContentType, LearningHeader } from '@atlassian/learning-components';
import { useLocation, useNavigate } from 'react-router-dom';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { ContentfullCourse, ContentfulLearningPath } from 'src/graphql/queries/GetLearningPath';
import { getInspectModeData } from 'src/utils/contentful';
import { User } from 'src/types';
import { sendTrackEvent, sendUIEvent } from 'src/utils/analytics';
import { FEEDBACK_PAGE_FF } from 'src/constants';
import {
  LearningProgressStatus,
  mapServerStatusToComponentsStatus,
  ProgressForCourses,
} from 'src/services/http/progress';
import { getSlug } from 'src/utils/slug';
import { getCourseUrl, getPathUrl, withPrefix } from 'src/utils/getUrl';

export interface LearningPathHeaderProps {
  learningPath: ContentfulLearningPath;
  user: User | null | undefined;
  onLoginUser: () => void;
  onSave: () => void;
  isLearningPathSaved: boolean;
  progress?: ProgressForCourses;
  isLoadingProgress: boolean;
  isLoading: boolean;
}

export const LearningPathHeader: React.FC<LearningPathHeaderProps> = ({
  learningPath,
  onLoginUser,
  user,
  onSave,
  isLearningPathSaved,
  progress,
  isLoadingProgress,
  isLoading,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isFeedbackPageAccessible = FeatureGates.checkGate(FEEDBACK_PAGE_FF);

  const learningPathSlug = getSlug(learningPath);

  const handleSignIn = useCallback(() => onLoginUser(), [onLoginUser]);

  const navigateToCourse = useCallback(
    (courseSlug: string) => navigate(withPrefix(getCourseUrl(courseSlug), location.pathname.replace(/\/$/, ''))),
    [navigate, location],
  );

  const findCourseToRedirect = useCallback(
    (courses: ContentfullCourse[], status: LearningProgressStatus): ContentfullCourse | undefined =>
      courses.find((course) => progress?.courses.some((p) => p.courseId === course.sys.id && p.status === status)),
    [progress],
  );

  const startLearningPath = useCallback(() => {
    const courses = learningPath?.coursesCollection?.items;

    if (!courses?.length) {
      return;
    }

    const hasNotStartedLearningPath =
      !progress?.overallStatus || progress.overallStatus === LearningProgressStatus.NOT_STARTED;

    const shouldNavigateToFirstCourse =
      hasNotStartedLearningPath || progress.overallStatus === LearningProgressStatus.COMPLETED;

    if (hasNotStartedLearningPath) {
      sendTrackEvent(
        'learningApp',
        'learningPath',
        'started',
        {
          learningPathSlug,
        },
        user?.account_id,
      );
    }

    const firstCourseSlug = getSlug(courses[0]);

    if (shouldNavigateToFirstCourse) {
      navigateToCourse(firstCourseSlug);

      return;
    }

    const inProgressCourse = findCourseToRedirect(courses, LearningProgressStatus.IN_PROGRESS);

    if (inProgressCourse) {
      navigateToCourse(getSlug(inProgressCourse));

      return;
    }

    const notStartedCourse = findCourseToRedirect(courses, LearningProgressStatus.NOT_STARTED);

    if (notStartedCourse) {
      navigateToCourse(getSlug(notStartedCourse));

      return;
    }

    // just a safe measure
    navigateToCourse(firstCourseSlug);
  }, [learningPath, progress, navigateToCourse, findCourseToRedirect, user, learningPathSlug]);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave();
      sendUIEvent('learningPathScreen', 'button', 'clicked', 'saveForLaterButton', {
        resourceSlug: learningPathSlug,
        resourceType: 'learningPath',
      });
    }
  }, [onSave, learningPathSlug]);

  // Add conditions when the survey is completed the button should not be shown
  const shouldShowFeedback = progress?.overallStatus === LearningProgressStatus.COMPLETED && isFeedbackPageAccessible;

  const navigateToFeedbackPage = useCallback(() => {
    const url = getPathUrl(learningPathSlug.replace(/\/$/, ''), true);

    window.open(url, '_blank');
  }, [learningPathSlug]);

  return (
    <div data-testid="learning-path-header">
      <LearningHeader
        inspectMode={getInspectModeData(learningPath || { sys: { id: '' } }, ['title'])}
        anonymousView={!user?.account_id}
        heading={learningPath?.title || 'Learning Path Title Not Found'}
        urlToShare={globalThis?.location?.href}
        onSignIn={handleSignIn}
        onStartCourse={startLearningPath}
        progressStatus={mapServerStatusToComponentsStatus(progress?.overallStatus)}
        isLoading={isLoading || isLoadingProgress}
        tagLine=""
        contentType={ContentType.LEARNING_PATH}
        onSave={handleSave}
        isSaved={isLearningPathSaved}
        shouldShowFeedback={shouldShowFeedback}
        onStartFeedback={navigateToFeedbackPage}
      />
    </div>
  );
};
